import { forwardRef, useState, useEffect } from 'react';
import { cn } from '../../utils/cn';
import type { NavItem } from './Navbar.types';
import type { BaseComponentProps } from '../../types';

export interface MobileMenuProps extends BaseComponentProps {
  /**
   * Menu aperto/chiuso
   */
  isOpen: boolean;
  /**
   * Callback per chiudere menu
   */
  onClose: () => void;
  /**
   * Items del menu
   */
  items: NavItem[];
  /**
   * Azioni (bottoni)
   */
  actions?: React.ReactNode;
}

/**
 * MobileMenu Component
 * 
 * Menu mobile fullscreen per Navbar.
 * Supporta dropdown nested.
 */
export const MobileMenu = forwardRef<HTMLDivElement, MobileMenuProps>(
  (
    {
      className,
      isOpen,
      onClose,
      items,
      actions,
      ...props
    },
    ref
  ) => {
    const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);

    // Chiudi menu quando si preme ESC
    useEffect(() => {
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && isOpen) {
          onClose();
        }
      };

      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    // Previeni scroll del body quando menu aperto
    useEffect(() => {
      if (isOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }

      return () => {
        document.body.style.overflow = '';
      };
    }, [isOpen]);

    const toggleSubmenu = (label: string) => {
      setOpenSubmenu(openSubmenu === label ? null : label);
    };

    return (
      <>
        {/* Overlay */}
        {isOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden transition-opacity"
            onClick={onClose}
            aria-hidden="true"
          />
        )}

        {/* Menu */}
        <div
          ref={ref}
          className={cn(
            'fixed top-0 right-0 h-full w-full max-w-sm',
            'bg-base-100 shadow-2xl z-50',
            'transform transition-transform duration-300 ease-in-out',
            'overflow-y-auto',
            isOpen ? 'translate-x-0' : 'translate-x-full',
            'lg:hidden',
            className
          )}
          {...props}
        >
          {/* Header con bottone close */}
          <div className="flex justify-end p-4 border-b border-base-300">
            <button
              onClick={onClose}
              className="p-2 hover:bg-base-200 rounded-lg transition-colors"
              aria-label="Chiudi menu"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Menu Items */}
          <nav className="p-4 space-y-2">
            {items.map((item) => (
              <div key={item.label}>
                {item.children ? (
                  // Item con submenu
                  <div>
                    <button
                      onClick={() => toggleSubmenu(item.label)}
                      className={cn(
                        'w-full flex items-center justify-between p-3 rounded-lg',
                        'hover:bg-base-200 transition-colors text-base-content'
                      )}
                    >
                      <span className="flex items-center gap-2">
                        {item.icon}
                        {item.label}
                        {item.badge && (
                          <span className="px-2 py-0.5 text-xs bg-primary text-primary-content rounded-full">
                            {item.badge}
                          </span>
                        )}
                      </span>
                      <svg
                        className={cn(
                          'w-5 h-5 transition-transform',
                          openSubmenu === item.label && 'rotate-180'
                        )}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    
                    {/* Submenu */}
                    {openSubmenu === item.label && (
                      <div className="ml-4 mt-2 space-y-2">
                        {item.children.map((child) => (
                          <a
                            key={child.label}
                            href={child.href}
                            className="block p-2 pl-4 rounded-lg hover:bg-base-200 transition-colors text-base-content/80"
                            onClick={onClose}
                          >
                            {child.label}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  // Item semplice
                  <a
                    href={item.href}
                    className={cn(
                      'flex items-center gap-2 p-3 rounded-lg',
                      'hover:bg-base-200 transition-colors text-base-content'
                    )}
                    onClick={onClose}
                  >
                    {item.icon}
                    {item.label}
                    {item.badge && (
                      <span className="px-2 py-0.5 text-xs bg-primary text-primary-content rounded-full">
                        {item.badge}
                      </span>
                    )}
                  </a>
                )}
              </div>
            ))}
          </nav>

          {/* Actions */}
          {actions && (
            <div className="p-4 border-t border-base-300">
              {actions}
            </div>
          )}
        </div>
      </>
    );
  }
);

MobileMenu.displayName = 'MobileMenu';

